import { LOGIN as AUTH_STORAGE_KEY } from 'app/ducks/auth/types'
import * as types from './types'

// { user_id, flags: { [flagName]: boolean }, timestamp }
const emptyState = {
  user_id: null,
  flags: {},
  timestamp: null,
}

const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key))
  } catch (e) {
    return null
  }
}

/**
 * The last successfully fetched flags, as persisted in localStorage — but
 * only when they belong to the user whose session is also persisted (see
 * ducks/auth/reducers.js). That way a reload renders straight away with
 * the last known flags, and getUserInfo's fetchFeatureFlags then refreshes
 * them in the background, instead of booting with every flag off.
 */
export const loadCachedState = () => {
  const cached = readJSON(types.STORAGE_KEY)
  const sessionUserId = readJSON(AUTH_STORAGE_KEY)?.userInfo?.id

  if (
    !cached ||
    typeof cached.flags !== 'object' ||
    cached.user_id == null ||
    cached.user_id !== sessionUserId
  )
    return emptyState

  return {
    user_id: cached.user_id,
    flags: cached.flags || {},
    timestamp: cached.timestamp ?? null,
  }
}

const featureFlagsReducer = (state = loadCachedState(), action) => {
  switch (action.type) {
    case types.FETCH_FEATURE_FLAGS_SUCCESS: {
      const newState = {
        user_id: action.payload.userId,
        flags: action.payload.flags,
        timestamp: action.payload.timestamp,
      }
      try {
        localStorage.setItem(types.STORAGE_KEY, JSON.stringify(newState))
      } catch (e) {
        // Storage full/blocked: the in-memory store still works, the
        // next reload just starts without a cache.
      }
      return newState
    }

    case types.CLEAR_FEATURE_FLAGS:
      try {
        localStorage.removeItem(types.STORAGE_KEY)
      } catch (e) {
        // See above.
      }
      return emptyState

    // A failed fetch with nothing cached: flags are now known to be
    // unavailable rather than still on their way, so flag-gated routes stop
    // waiting (see selectors.js#areSettled) and resolve as off. With a
    // cache, the failure falls through to `default` below instead.
    case types.FETCH_FEATURE_FLAGS_ERROR:
      return state.timestamp ? state : { ...state, fetch_failed: true }

    // Everything else (notably auth's LOGOUT — this reducer never listens
    // for it) intentionally falls through here: the cache from a previous
    // successful fetch persists untouched, both across a failed re-fetch
    // and across logout.
    default:
      return state
  }
}

export default featureFlagsReducer
